import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "../styles/SpeakerProfile.css";
import { API_BASE_URL } from "../config";

const Shimmer = ({ className = "", style = {} }) => (
  <span
    className={`sp-shimmer ${className}`}
    style={style}
    aria-hidden="true"
  />
);

const HeroSkeleton = () => (
  <div className="sp-hero sp-hero--skeleton" aria-hidden="true">
    <div className="sp-container">
      <div className="sp-hero__inner">
        <div className="sp-hero__avatar-wrap">
          <Shimmer
            className="sp-shimmer--circle sp-shimmer--dark"
            style={{ width: 124, height: 124 }}
          />
        </div>
        <div className="sp-hero__identity">
          <Shimmer
            className="sp-shimmer--dark"
            style={{
              width: "55%",
              height: 32,
              marginBottom: 12,
              borderRadius: 6,
            }}
          />
          <Shimmer
            className="sp-shimmer--dark"
            style={{
              width: "42%",
              height: 18,
              marginBottom: 10,
              borderRadius: 4,
            }}
          />
          <div style={{ display: "flex", gap: 10 }}>
            {[1, 2, 3].map((i) => (
              <Shimmer
                key={i}
                className="sp-shimmer--circle sp-shimmer--dark"
                style={{ width: 38, height: 38 }}
              />
            ))}
          </div>
        </div>
        <div className="sp-hero__stats">
          <Shimmer
            className="sp-shimmer--dark"
            style={{ width: 90, height: 80, borderRadius: 8 }}
          />
        </div>
      </div>
    </div>
  </div>
);

const MainColSkeleton = () => (
  <div className="sp-main-col">
    <div className="sp-section">
      <Shimmer
        style={{ width: "28%", height: 22, marginBottom: 20, borderRadius: 4 }}
      />
      {[100, 96, 88, 72, 94, 60].map((w, i) => (
        <Shimmer
          key={i}
          style={{
            width: `${w}%`,
            height: 14,
            marginBottom: 10,
            borderRadius: 4,
          }}
        />
      ))}
    </div>
    <div className="sp-section">
      <Shimmer
        style={{ width: "34%", height: 22, marginBottom: 20, borderRadius: 4 }}
      />
      {[1, 2].map((card) => (
        <div
          key={card}
          className="sp-session-card"
          style={{ marginBottom: card === 1 ? 20 : 0 }}
        >
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            {[72, 88, 78].map((w, i) => (
              <Shimmer
                key={i}
                style={{ width: w, height: 22, borderRadius: 20 }}
              />
            ))}
          </div>
          <Shimmer
            style={{
              width: "82%",
              height: 20,
              marginBottom: 10,
              borderRadius: 4,
            }}
          />
          <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
            {[145, 75, 62].map((w, i) => (
              <Shimmer
                key={i}
                style={{ width: w, height: 13, borderRadius: 4 }}
              />
            ))}
          </div>
          {[100, 96, 78].map((w, i) => (
            <Shimmer
              key={i}
              style={{
                width: `${w}%`,
                height: 13,
                marginBottom: 8,
                borderRadius: 4,
              }}
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);

const SidebarSkeleton = () => (
  <aside className="sp-sidebar-col">
    <div className="sp-sidebar-card">
      <Shimmer
        style={{ width: "38%", height: 16, marginBottom: 14, borderRadius: 4 }}
      />
      <Shimmer
        style={{ width: "78%", height: 18, marginBottom: 12, borderRadius: 4 }}
      />
      {[1, 2, 3].map((i) => (
        <Shimmer
          key={i}
          style={{
            width: "85%",
            height: 13,
            marginBottom: 10,
            borderRadius: 4,
          }}
        />
      ))}
      <Shimmer
        style={{ width: "100%", height: 38, marginTop: 6, borderRadius: 8 }}
      />
    </div>
    <div className="sp-sidebar-card">
      <Shimmer
        style={{ width: "42%", height: 16, marginBottom: 14, borderRadius: 4 }}
      />
      <div style={{ display: "flex", gap: 10 }}>
        {[1, 2, 3, 4].map((i) => (
          <Shimmer
            key={i}
            className="sp-shimmer--circle"
            style={{ width: 38, height: 38 }}
          />
        ))}
      </div>
    </div>
  </aside>
);

// Updated SocialLink component - now shows "X" for Twitter
const SocialLink = ({ href, icon, label, platform }) => {
  if (!href) return null;
  
  // Get the appropriate SVG icon
  const getSVGIcon = () => {
    switch(platform) {
      case 'twiter':
        // X (Twitter) logo
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        );
      case 'linkedin':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
          </svg>
        );
      case 'github':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.15 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.62.24 2.85.12 3.15.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="sp-social-link"
      aria-label={label}
      title={label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '30px',
        height: '30px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.08)',
        color: '#e4c896',
        transition: 'all 0.2s ease',
        textDecoration: 'none',
        border: '1px solid rgba(228, 200, 150, 0.15)',
        hover: {
          background: 'rgba(228, 200, 150, 0.15)',
          transform: 'translateY(-2px)'
        }
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(228, 200, 150, 0.15)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {getSVGIcon() || <span>🔗</span>}
    </a>
  );
};

const SessionCard = ({ session }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="sp-session-card">
      <h3 className="sp-session-title">{session.title}</h3>
      {session.abstract && (
        <>
          <p
            className={`sp-session-abstract ${expanded ? "sp-session-abstract--expanded" : ""}`}
          >
            {session.abstract}
          </p>
          {session.abstract.length > 160 && (
            <button
              type="button"
              className="sp-expand-btn"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? "Show less" : "Read more"}
              <i className={`fas fa-chevron-${expanded ? "up" : "down"}`} />
            </button>
          )}
        </>
      )}
    </div>
  );
};

const ErrorState = ({ message, onBack }) => (
  <div className="sp-page">
    <div
      className="sp-container"
      style={{ padding: "80px 20px", textAlign: "center" }}
    >
      <div style={{ fontSize: "3rem", marginBottom: "16px", color: "#f56565" }}>
        <i className="fas fa-exclamation-circle" />
      </div>
      <h2 style={{ marginBottom: "8px" }}>Speaker Not Found</h2>
      <p style={{ color: "#718096", marginBottom: "24px" }}>{message}</p>
      <button className="sp-btn sp-btn--outline" onClick={onBack}>
        <i className="fas fa-arrow-left" /> Go Back
      </button>
    </div>
  </div>
);

const SpeakerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [speakerData, setSpeakerData] = useState(null);
  const [eventData, setEventData] = useState(null);

  useEffect(() => {
    if (!id) {
      setError("No speaker ID provided.");
      setIsLoading(false);
      return;
    }

    const fetchSpeaker = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        const response = await fetch(`${API_BASE_URL}/getSpeakerDetailsById/${id}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load speaker (HTTP ${response.status})`);
        }

        const data = await response.json();
        setSpeakerData(data);
      } catch (err) {
        console.error("Error fetching speaker:", err);
        setError(err.message || "Failed to load speaker profile.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSpeaker();
  }, [id]);

  useEffect(() => {
    if (!speakerData?.event_id) return;

    const fetchEvent = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/event/${speakerData.event_id}`);
        const data = await res.json();
        setEventData(data);
      } catch (err) {
        console.error("Error fetching event:", err);
      }
    };

    fetchEvent();
  }, [speakerData]);

  const eventName = eventData?.metadata?.name;
  const venue = eventData?.metadata?.venue;
  const city = eventData?.metadata?.city;
  const startDate = eventData?.metadata?.start_date;
  const endDate = eventData?.metadata?.end_date;

  const formatDateOnly = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getDerivedData = () => {
    if (!speakerData) return null;

    // Access the additional_info object from the backend
    const additional = speakerData.additional_info || {};

    const talkDetails = speakerData.talk_details || {};
    const sessions = [];
    if (
      talkDetails["Talk Topic"] ||
      talkDetails["Talk Title"] ||
      talkDetails["Talk Details"]
    ) {
      sessions.push({
        id: 1,
        title:
          talkDetails["Talk Topic"] ||
          talkDetails["Talk Title"] ||
          talkDetails["Talk Toipc"] ||
          "Untitled Talk",
        abstract: talkDetails["Talk Details"] || "",
      });
    }

    // Social media links - backend uses "twitter" but we'll display as "X"
    const social = {
      twiter: additional.twiter || additional["twiter"] || null,
      linkedin: additional.linkedin || additional["linkedin"] || null,
      github: additional.github || additional["github"] || null,
    };

    const event = {
      id: speakerData.event_id,
      name: eventName,
      venue: venue,
      city: city,
      date: startDate && endDate
        ? `${formatDateOnly(startDate)} - ${formatDateOnly(endDate)}`
        : formatDateOnly(startDate),
    };

    return { sessions, social, event };
  };

  if (!isLoading && error) {
    return <ErrorState message={error} onBack={() => navigate(-1)} />;
  }

  const derived = !isLoading && speakerData ? getDerivedData() : null;

  return (
    <div className="sp-page">
      {isLoading ? (
        <HeroSkeleton />
      ) : (
        <div className="sp-profile-sidebar">
          <button className="go-back" onClick={() => navigate(-1)}>
            <i className="fa-solid fa-arrow-left" /> Back to Speakers
          </button>

          <div className="sp-hero__inner">
            <div className="sp-hero__avatar-wrap">
              {speakerData.profile_picture?.base64 ? (
                <img
                  src={speakerData.profile_picture.base64}
                  alt={"profile photo"}
                  className="sp-hero__avatar"
                  loading="eager"
                />
              ) : (
                <div
                  className="sp-hero__avatar sp-hero__avatar--fallback"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, #1c2338, #0d1220)",
                    color: "#e4c896",
                    fontSize: "3.5rem",
                    fontWeight: 600,
                    fontFamily: "'Fraunces', Georgia, serif",
                    borderRadius: "50%",
                  }}
                >
                  {speakerData.name
                    ? speakerData.name.charAt(0).toUpperCase()
                    : "S"}
                </div>
              )}
            </div>

            <div className="sp-hero__identity">
              <span className="sp-eyebrow">Speaker</span>
              <h1 className="sp-hero__name">
                {speakerData.name || "Speaker"}
              </h1>

              <p className="sp-hero__role">
                {speakerData.jobtitle && (
                  <>
                    {speakerData.jobtitle}
                    {speakerData.company && (
                      <>
                        <span className="sp-hero__role-sep">·</span>
                        <span className="sp-hero__org">
                          {speakerData.company}
                        </span>
                      </>
                    )}
                  </>
                )}
                {!speakerData.jobtitle && speakerData.company && (
                  <span className="sp-hero__org">{speakerData.company}</span>
                )}
              </p>

              <div
                className="sp-hero__social"
                aria-label="Social media links"
                style={{
                  display: 'flex',
                  gap: '10px',
                  marginTop: '12px'
                }}
              >
                {/* Twitter/X - backend uses "twitter" but we display as X */}
                <SocialLink
                  href={derived?.social?.twiter}
                  platform="twiter"
                  label="X (Twiter) profile"
                />
                <SocialLink
                  href={derived?.social?.linkedin}
                  platform="linkedin"
                  label="LinkedIn profile"
                />
                <SocialLink
                  href={derived?.social?.github}
                  platform="github"
                  label="GitHub profile"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="sp-body sp-new-layout">
        <div className="sp-container">
          {isLoading ? (
            <div className="sp-body__grid">
              <MainColSkeleton />
              <SidebarSkeleton />
            </div>
          ) : (
            <div className="sp-body__grid">
              <div className="sp-main-col">
                {speakerData.biography && (
                  <section className="sp-section" aria-labelledby="bio-heading">
                    <h2 className="sp-section__title" id="bio-heading">
                      <i className="fas fa-user" /> About
                    </h2>
                    <div className="sp-bio">
                      {speakerData.biography.split("\n\n").map((para, idx) => (
                        <p key={idx}>{para}</p>
                      ))}
                    </div>
                  </section>
                )}

                {speakerData?.talk_details?.["Talk details"] && (
                  <section className="sp-section">
                    <h2 className="sp-section__title">
                      <i className="fas fa-file-alt" /> Talk Details
                    </h2>
                    <div className="sp-bio">
                      <p>{speakerData.talk_details["Talk details"]}</p>
                    </div>
                  </section>
                )}

                {derived?.sessions?.length > 0 && (
                  <section
                    className="sp-section"
                    aria-labelledby="sessions-heading"
                  >
                    <h2 className="sp-section__title" id="sessions-heading">
                      <i className="fas fa-microphone" /> Speaking Session
                    </h2>
                    <div className="sp-sessions-list">
                      {derived.sessions.map((session) => (
                        <SessionCard key={session.id} session={session} />
                      ))}
                    </div>
                  </section>
                )}
              </div>

              <aside className="sp-sidebar-col" aria-label="Sidebar">
                <div className="sp-sidebar-card">
                  <h3 className="sp-sidebar-card__title">
                    <i className="fas fa-calendar-alt" /> Event
                  </h3>

                  {eventData?.banner_file?.base64 && (
                    <img
                      src={eventData.banner_file.base64}
                      alt={derived?.event?.name}
                      className="sp-event-banner"
                    />
                  )}

                  {derived?.event?.id ? (
                    <Link
                      to={`/eventpage/${derived.event.id}`}
                      className="sp-event-link"
                    >
                      {derived.event.name || "Event"}
                    </Link>
                  ) : (
                    <span
                      className="sp-event-link"
                      style={{ cursor: "default" }}
                    >
                      {derived?.event?.name || "Event"}
                    </span>
                  )}
                  <ul className="sp-event-details">
                    <li>
                      <i className="fas fa-calendar" />
                      {derived?.event?.date || "Date not available"}
                    </li>
                    <li>
                      <i className="fas fa-map-marker-alt" />
                      {derived?.event?.venue || "Venue not available"}
                    </li>
                    <li>
                      <i className="fas fa-city" />
                      {derived?.event?.city || "City not available"}
                    </li>
                  </ul>
                  {derived?.event?.id && (
                    <Link
                      to={`/eventpage/${derived.event.id}`}
                      className="sp-btn sp-btn--outline"
                    >
                      View Event Details
                    </Link>
                  )}
                </div>
              </aside>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SpeakerProfile;